export type DatafastFieldKey = "cardHolder" | "cardNumber" | "expiry" | "cvv";

export interface DatafastMissingField {
	key: DatafastFieldKey;
	label: string;
	selectors: string[];
}

export interface DatafastValidationResult {
	valid: boolean;
	missingFields: DatafastMissingField[];
}

interface FieldConfig {
	label: string;
	dataPaths: string[];
	selectors: string[];
	normalize?: (value: string | null) => string | null;
}

const FIELD_CONFIG: Record<DatafastFieldKey, FieldConfig> = {
	cardHolder: {
		label: "nombre en la tarjeta",
		dataPaths: [
			"cardHolder",
			"card.holder",
			"card.holderName",
			"card.holder_name",
			"holder",
			"holderName",
		],
		selectors: [
			'input[name="card.holder"]',
			'input[name="cardHolder"]',
			'.wpwl-control-cardHolder input',
			'.wpwl-control-cardHolder .wpwl-control',
		],
	},
	cardNumber: {
		label: "número de la tarjeta",
		dataPaths: [
			"cardNumber",
			"card.number",
			"card.pan",
			"number",
		],
		selectors: [
			'input[name="card.number"]',
			'input[name="cardNumber"]',
			'iframe[name="card.number"]',
			'iframe[name="cardNumber"]',
			'.wpwl-control-cardNumber input',
			'.wpwl-control-cardNumber iframe',
			'.wpwl-control-cardNumber .wpwl-control',
		],
		normalize: (value) => {
			if (!value) {
				return null;
			}
			const digits = value.replace(/\D/g, "");
			return digits.length > 0 ? digits : null;
		},
	},
	expiry: {
		label: "fecha de expiración",
		dataPaths: [
			"expiry",
			"card.expiry",
			"card.expirationDate",
			"card.expiryDate",
		],
		selectors: [
			'input[name="card.expiry"]',
			'.wpwl-control-expiry input',
			'.wpwl-control-expiry .wpwl-control',
			'input[name="expiry.month"]',
			'input[name="expiry.year"]',
			'select[name="card.expiryMonth"]',
			'select[name="card.expiryYear"]',
		],
	},
	cvv: {
		label: "CVV",
		dataPaths: [
			"cvv",
			"card.cvv",
			"card.securityCode",
			"card.cvc",
		],
		selectors: [
			'input[name="card.cvv"]',
			'input[name="cvv"]',
			'input[name="card.cvc"]',
			'iframe[name="card.cvv"]',
			'iframe[name="card.cvc"]',
			'.wpwl-control-cvv input',
			'.wpwl-control-cvv iframe',
			'.wpwl-control-cvv .wpwl-control',
		],
		normalize: (value) => {
			if (!value) {
				return null;
			}
			const digits = value.replace(/\D/g, "");
			return digits.length > 0 ? digits : null;
		},
	},
};

export const INSTALLMENT_OPTIONS: number[] = [0, 3, 6, 9, 12];

export const CREDIT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "00", label: "Transacción corriente" },
	{ value: "01", label: "Diferido corriente" },
	{ value: "02", label: "Diferido con interés" },
	{ value: "03", label: "Diferido sin interés" },
	{ value: "07", label: "Diferido con interés + meses de gracia" },
	{ value: "09", label: "Diferido sin interés + meses de gracia" },
	{ value: "21", label: "Diferido Plus" },
	{ value: "22", label: "Duplica tu plazo" },
];

const EXPIRY_MONTH_PATHS = [
	"expiryMonth",
	"card.expiryMonth",
	"expiry.month",
	"card.expiry.month",
];

const EXPIRY_YEAR_PATHS = [
	"expiryYear",
  	"card.expiryYear",
	"expiry.year",
	"card.expiry.year",
];

const EXPIRY_MONTH_SELECTORS = [
	'input[name="card.expiryMonth"]',
	'input[name="expiry.month"]',
	'select[name="card.expiryMonth"]',
	'.wpwl-control-expiry select[name*="expiryMonth"]',
];

const EXPIRY_YEAR_SELECTORS = [
	'input[name="card.expiryYear"]',
	'input[name="expiry.year"]',
	'select[name="card.expiryYear"]',
	'.wpwl-control-expiry select[name*="expiryYear"]',
];

const getValueFromData = (data: unknown, paths: string[]): string | null => {
	if (!data) {
		return null;
	}

	const tryResolveFromObject = (source: Record<string, unknown>, path: string): string | null => {
		if (path in source) {
			const direct = source[path];
			if (typeof direct === "string" || typeof direct === "number") {
				const trimmed = String(direct).trim();
				return trimmed === "" ? null : trimmed;
			}
		}

		const underscored = path.replace(/\./g, "_");
		if (underscored in source) {
			const value = source[underscored];
			if (typeof value === "string" || typeof value === "number") {
				const trimmed = String(value).trim();
				return trimmed === "" ? null : trimmed;
			}
		}

		const segments = path.split(".");
		let current: any = source;

		for (const segment of segments) {
			if (current && typeof current === "object" && segment in current) {
				current = current[segment];
			} else {
				current = undefined;
				break;
			}
		}

		if (typeof current === "string" || typeof current === "number") {
			const trimmed = String(current).trim();
			return trimmed === "" ? null : trimmed;
		}

		return null;
	};

	for (const path of paths) {
		let resolved: string | null = null;

		if (Array.isArray(data)) {
			for (const item of data) {
				if (!item || typeof item !== "object") {
					continue;
				}

				const record = item as Record<string, unknown>;

				const keyCandidates = [
					record.name,
					record.field,
					record.id,
					record.key,
				].filter((candidate): candidate is string => typeof candidate === "string");

				for (const key of keyCandidates) {
					if (key === path || key === path.replace(/\./g, "_")) {
						const value =
							record.value ??
							record[path] ??
							record[path.replace(/\./g, "_")];

						if (typeof value === "string" || typeof value === "number") {
							const trimmed = String(value).trim();
							if (trimmed !== "") {
								return trimmed;
							}
						}
					}
				}

				const direct = tryResolveFromObject(record, path);
				if (direct) {
					return direct;
				}
			}
		} else if (typeof data === "object") {
			resolved = tryResolveFromObject(data as Record<string, unknown>, path);
		}

		if (resolved) {
			return resolved;
		}
	}

	return null;
};

const getValueFromSelectors = (selectors: string[]): string | null => {
	if (typeof document === "undefined") {
		return null;
	}

	for (const selector of selectors) {
		const element = document.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null;
		if (element && "value" in element) {
			const value = (element.value || "").trim();
			if (value !== "") {
				return value;
			}
		}
	}

	return null;
};

const normalizeExpiry = (value: string | null): { month: string | null; year: string | null } => {
	if (!value) {
		return { month: null, year: null };
	}

	const cleaned = value.replace(/\s/g, "");

	if (cleaned.includes("/")) {
		const [month, year] = cleaned.split("/");
		return {
			month: month ?? null,
			year: year ?? null,
		};
	}

	if (cleaned.length === 4) {
		return {
			month: cleaned.slice(0, 2),
			year: cleaned.slice(2),
		};
	}

	return { month: null, year: null };
};

const isFieldValid = (key: DatafastFieldKey, value: string | null, rawValue: string | null): boolean => {
	const fallback = rawValue ?? value;

	if (!fallback || fallback.trim().length === 0) {
		return false;
	}

	const valueToCheck = (value ?? fallback).trim();

	if (key === "cardHolder") {
		return valueToCheck.length > 2;
	}

	if (key === "cardNumber") {
		const digits = valueToCheck.replace(/\D/g, "");

		if (valueToCheck.includes("*")) {
			return true;
		}

		if (digits.length >= 4 && valueToCheck.length >= 4) {
			return true;
		}

		return digits.length >= 12;
	}

	if (key === "cvv") {
		const digits = valueToCheck.replace(/\D/g, "");
		if (digits.length >= 3) {
			return true;
		}

		return valueToCheck.includes("*") && valueToCheck.length >= 3;
	}

	if (key === "expiry") {
		const { month, year } = normalizeExpiry(valueToCheck);
		return Boolean(month && month.length === 2 && year && year.length >= 2);
	}

	return valueToCheck.length > 0;
};

export const validateDatafastCardFields = (data?: unknown): DatafastValidationResult => {
	const missingFields: DatafastMissingField[] = [];

	const checkField = (key: DatafastFieldKey): void => {
		const config = FIELD_CONFIG[key];
		const rawValue =
			getValueFromData(data, config.dataPaths) ??
			getValueFromSelectors(config.selectors);

		const normalized = config.normalize ? config.normalize(rawValue) : rawValue;
		const valueToValidate = normalized ?? rawValue;

		if (
			(valueToValidate === null ||
				valueToValidate === undefined ||
				(typeof valueToValidate === "string" && valueToValidate.trim() === "")) &&
			(key === "cardNumber" || key === "cvv")
		) {
			return;
		}

		if (!isFieldValid(key, normalized, rawValue)) {
			missingFields.push({
				key,
				label: config.label,
				selectors: config.selectors,
			});
		}
	};

	checkField("cardHolder");
	checkField("cardNumber");

	const month =
		getValueFromData(data, EXPIRY_MONTH_PATHS) ??
		getValueFromSelectors(EXPIRY_MONTH_SELECTORS);
	const year =
		getValueFromData(data, EXPIRY_YEAR_PATHS) ??
		getValueFromSelectors(EXPIRY_YEAR_SELECTORS);

	if (month && year) {
		const combined = `${month}/${year}`;
		if (!isFieldValid("expiry", combined, combined)) {
			missingFields.push({
				key: "expiry",
				label: FIELD_CONFIG.expiry.label,
				selectors: Array.from(new Set([
					...FIELD_CONFIG.expiry.selectors,
					...EXPIRY_MONTH_SELECTORS,
					...EXPIRY_YEAR_SELECTORS,
				])),
			});
		}
	} else {
		checkField("expiry");
	}

	checkField("cvv");

	return {
		valid: missingFields.length === 0,
		missingFields,
	};
};

export const focusFirstMissingDatafastField = (missingFields: DatafastMissingField[]): void => {
	if (typeof document === "undefined") {
		return;
	}

	for (const field of missingFields) {
		for (const selector of field.selectors) {
			const element = document.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null;
			if (element && typeof element.focus === "function") {
				setTimeout(() => element.focus(), 0);
				return;
			}
		}
	}
};

const INLINE_ERROR_CLASS = "bcommerce-datafast-error";
const DEFAULT_ERROR_COLOR = "#d93025";

const applyErrorStyles = (container: HTMLElement, reference?: HTMLElement | null): void => {
	if (typeof window === "undefined") {
		container.style.color = DEFAULT_ERROR_COLOR;
		return;
	}

	let color: string | null = null;
	const form = getPaymentWidgetForm();
	const fallbackReference = form?.querySelector<HTMLElement>(".wpwl-hint-error, .wpwl-hint");
	if (fallbackReference) {
		const fallbackStyles = window.getComputedStyle(fallbackReference);
		if (fallbackStyles?.color && fallbackStyles.color !== "rgba(0, 0, 0, 0)") {
			color = fallbackStyles.color;
		}
		if (fallbackStyles?.fontSize && fallbackStyles.fontSize !== "0px") {
			container.style.fontSize = fallbackStyles.fontSize;
		}
	}

	if (reference) {
		const referenceStyles = window.getComputedStyle(reference);
		if (referenceStyles?.fontFamily) {
			container.style.fontFamily = referenceStyles.fontFamily;
		}
	}

	container.style.color = color || DEFAULT_ERROR_COLOR;
};

const getPaymentWidgetForm = (): HTMLFormElement | null => {
	if (typeof document === "undefined") {
		return null;
	}

	return document.querySelector("form.paymentWidgets");
};

const findFieldContainer = (field: DatafastMissingField): HTMLElement | null => {
	for (const selector of field.selectors) {
		const element = document.querySelector(selector) as HTMLElement | null;
		if (!element) {
			continue;
		}

		const group =
			element.closest(".wpwl-group") as HTMLElement | null ||
			(element.closest(".wpwl-wrapper")?.parentElement as HTMLElement | null);

		if (group) {
			return group;
		}
	}

	return null;
};

const ensureFieldErrorContainer = (host: HTMLElement): HTMLDivElement => {
	let container = host.querySelector(`.${INLINE_ERROR_CLASS}`) as HTMLDivElement | null;
	if (!container) {
		container = document.createElement("div");
		container.className = `wpwl-hint wpwl-hint-error wpwl-error ${INLINE_ERROR_CLASS}`;
		container.setAttribute("role", "alert");
		host.appendChild(container);
	}

	const referenceLabel = host.querySelector(".wpwl-label") as HTMLElement | null;
	applyErrorStyles(container, referenceLabel);

	return container;
};

const ensureFormErrorContainer = (form: HTMLFormElement): HTMLDivElement => {
	let container = form.querySelector(`.${INLINE_ERROR_CLASS}.form-level`) as HTMLDivElement | null;
	if (!container) {
		container = document.createElement("div");
		container.className = `wpwl-hint wpwl-hint-error wpwl-error ${INLINE_ERROR_CLASS} form-level`;
		container.setAttribute("role", "alert");

		const submitGroup = form.querySelector(".wpwl-group-submit");
		if (submitGroup && submitGroup.parentElement === form) {
			form.insertBefore(container, submitGroup);
		} else {
			form.appendChild(container);
		}
	}

	applyErrorStyles(container);

	return container;
};

const enableDatafastSubmitButton = (): void => {
	const form = getPaymentWidgetForm();
	const button =
		form?.querySelector<HTMLButtonElement>(".wpwl-button-pay") ??
		form?.querySelector<HTMLButtonElement>(".wpwl-button");
	if (button) {
		button.removeAttribute("disabled");
		button.classList.remove("wpwl-button-error");
	}
};

export const showInlineDatafastError = (
	message: string,
	field?: DatafastMissingField
): void => {
	if (typeof document === "undefined") {
		return;
	}

	// Limpia mensajes anteriores para evitar duplicados visibles
	clearInlineDatafastError();

	if (field) {
		const fieldContainer = findFieldContainer(field);
		if (fieldContainer) {
			const errorNode = ensureFieldErrorContainer(fieldContainer);
			fieldContainer.classList.add("wpwl-has-error");
			errorNode.textContent = message;
			errorNode.style.display = "block";
			return;
		}
	}

	const form = getPaymentWidgetForm();
	if (!form) {
		return;
	}

	const fallback = ensureFormErrorContainer(form);
	fallback.textContent = message;
	fallback.style.display = "block";
	enableDatafastSubmitButton();
};

export const clearInlineDatafastError = (): void => {
	if (typeof document === "undefined") {
		return;
	}

	const nodes = document.querySelectorAll(`.${INLINE_ERROR_CLASS}`);
	nodes.forEach((node) => {
		if (node instanceof HTMLElement) {
			node.textContent = "";
			node.style.display = "none";
		}
	});

	const errorGroups = document.querySelectorAll(".wpwl-group.wpwl-has-error");
	errorGroups.forEach((group) => group.classList.remove("wpwl-has-error"));

	enableDatafastSubmitButton();
};
